import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { lastValueFrom } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { CoreComponent } from 'src/app/core.component';
import { KeyValue } from '@angular/common';
import { ProductsMetaService } from '../../../cart/products-meta.service';
import { RainSystemMeasurementsDto, RoofColorDto, RoofFinishDto, RoofProducerEnum } from '../../../../api/models';
import { CalculationModelsState, selectModels, selectRainSystem } from '../../../calculation/calculation.state';
import { ProductsService } from '../../../../api/services/products.service';
import { AppState } from '../../../app.state';
import { RainSystemCustomizationsDto } from './rain-system-customizations.dto';

@Component({
  selector: 'rain-system-customizations',
  templateUrl: './rain-system-customizations.component.html',
  styleUrls: ['./rain-system-customizations.component.scss'],
})
export class RainSystemCustomizationsComponent extends CoreComponent implements OnInit {
  @Output()
  eventEmitter = new EventEmitter<RainSystemCustomizationsDto>();
  @Input()
  predefinedProducer = false;

  finishes: string[] = [];
  diameters: KeyValue<string, number>[] = [];
  colors: string[] = [];
  form!: UntypedFormGroup;

  private rainSystemState!: RainSystemMeasurementsDto;
  private customizations: any;

  constructor(
    private readonly store: Store<AppState>,
    private readonly _formBuilder: UntypedFormBuilder,
    private readonly productsService: ProductsService,
  ) {
    super();
  }

  async ngOnInit(): Promise<void> {
    this.setupForms();
    this.garbageCollector.push(
      this.store
        .pipe(
          map((state) => selectRainSystem(state)),
          filter((state) => !!state),
        )
        .subscribe((rainSystemState) => {
          this.rainSystemState = rainSystemState;
          const producer = this.rainSystemState.producer ?? RoofProducerEnum.Bilka;
          if (this.form.controls.producer.value !== producer) {
            this.form.setValue({
              producer,
              finish: this.rainSystemState.finish ?? '',
              color: this.rainSystemState.color ?? '',
              diameter: this.rainSystemState.diameter ?? '',
            });
            this.finishChange();
          }
        }),
    );
    this.garbageCollector.push(
      this.store
        .pipe(
          map((state) => selectModels(state)),
          filter((state) => !!state?.producer && this.form.controls.producer.value !== state.producer),
          map((state) => state as CalculationModelsState),
          debounceTime(400),
          distinctUntilChanged(),
        )
        .subscribe((modelsState) => {
          this.form.controls.producer.patchValue(modelsState.producer);
          this.populateCustomizations();
        }),
    );
    this.garbageCollector.push(
      this.form.valueChanges.pipe(debounceTime(400)).subscribe((value) => {
        if (this.form.valid) {
          this.eventEmitter.emit(value);
        }
      }),
    );
  }

  setupForms(): void {
    const config: any = {};
    config.finish = new UntypedFormControl();
    config.color = new UntypedFormControl();
    config.diameter = new UntypedFormControl();
    config.producer = new UntypedFormControl();
    this.form = this._formBuilder.group(config);
  }

  getImagePath(finish: string, color: string): string {
    return ProductsMetaService.getRainSystemImagePath(this.form.controls.producer.value, finish, color);
  }

  async producerChange() {
    await this.populateCustomizations();
  }

  finishChange() {
    this.populateCustomizations(this.form.controls.diameter.value, this.form.controls.finish.value);
  }
  diameterChange() {
    this.populateCustomizations(this.form.controls.diameter.value, this.form.controls.finish.value);
  }

  async getCustomizations() {
    this.customizations = await lastValueFrom(this.productsService.productsControllerGetRainSystemCustomizations());
  }
  async populateCustomizations(selectedDimension?: number, selectedFinish?: string) {
    if (!this.customizations) {
      await this.getCustomizations();
    }
    const producer = this.form.controls.producer.value;
    const productsByDimension = this.customizations[producer];
    const formValue = this.form.value;
    this.diameters = Object.keys(productsByDimension).map((key) => ({
      key,
      value: Number(productsByDimension[key].dimension),
    }));
    const selectedDimensionKey = (
      selectedDimension
        ? this.diameters.find((diameter) => diameter.value === selectedDimension)?.key
        : this.diameters[0].key
    ) as string;
    if (!formValue.diameter) {
      formValue.diameter = selectedDimension || this.diameters[0].value;
    }
    this.finishes = productsByDimension[selectedDimensionKey].finishes.map((finish: RoofFinishDto) => finish.finish);
    if (!formValue.finish || !this.finishes.includes(formValue.finish)) {
      formValue.finish = this.finishes[0];
    }

    this.colors =
      productsByDimension[selectedDimensionKey]?.finishes
        .find((finish: RoofFinishDto) => finish.finish === (selectedFinish || this.finishes[0]))
        ?.thicknesses[0].colors.map((color: RoofColorDto) => color.color) || [];
    if (!formValue.color || !this.colors.includes(formValue.color)) {
      formValue.color = this.colors[0];
    }
    this.form.setValue(formValue);
  }
}
